import { combineReducers, AnyAction } from 'redux';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import userDetailsReducer from './userDetailsSlice';
import redirectReducer from './redirectSlice';
import preloaderReducer from './preloaderReducer';
import eventDetailsSlice from './eventDetailsSlice';
import eventFiltersSlice from './eventFiltersSlice';
import domainsReducer from './domainsReducer';
import componentsDataReducer from './componentsDataReducer';
import entityInfoReducer from './entityInfoSlice';
import entitySettingsReducer from './entitySettingsSlice';
import entitySettingsOptionsReducer from './entitySettingsOptionsSlice';
import eventsLevelsSlice from './eventsLevelsSlice';
import updateHistorySlice from './updateHistorySlice';
import canbusTroubleshootReducer from './canbusTroubleshootReducer';
import canbusTroubleshootTutorialReducer from './canbusTroubleshootTutorialReducer';
import loadManagerReducer from './loadManagerReducer';
import entityKpiDetailsReducer from './entityKpiDetailsSlice';
import domainModalGridReducer from './domainModalGridReducer';
import dataChartReducer from './dataChartSlice';
import vectorDataChartSlice from './vectorDataChartSlice';
import vectorConfigurationReducer from './vectorConfigurationSlice';
import featureFlagsReducer from './featureFlagSlice';
import eventLevelMenuOptionsReducer from './eventsLevelMenuOptionsSlice';
import dataChartEventsReducer from './dataChartEventsSlice';
import {
  RESET_STORAGE,
  RESET_STORAGE_WITHOUT_USER_SESSION,
} from '../common/types';
import vectorDetailsReducer from './vectorDetailsSlice';
import zoomReducer from './zoomSlice';
import vectorCriticalAlarmsAndEventsReducer from './vectorCriticalAlarmsAndEventsSlice';
import tableColumnsReducer from './tableColumnsSlice';
import farmsOptionsReducer from './farmsOptionsSlice';
import astronautPropertySelectReducer from './astronautPropertySelectSlice';
import fencesStateReducer from './fencesStateSlice';
import appliedPropertiesReducer from './appliedPropertiesSlice';
import tooltipReducer from './tooltipSlice';
import lineChartConfigReducer from './lineChartConfigSlice';
import colorDictionaryReducer from './colorDictionarySlice';
import networkErrorReducer from './networkErrorSlice';
import dataExplorerLoadManagerReducer from './dataExplorerLoadManagerSlice';
import canvasGenerationReducer from './canvasGenerationSlice';
import astronautTooltipReducer from './astronautTooltipSlice';
import astronautSelectedActivityDetailsReducer from './astronautSelectedActivityDetailsSlice';
import verticalLineReducer from './verticalLineSlice';
import astronautGraphElementsReducer from './astronautGraphElementsSlice';
import kpiReducer from './kpiDataSlice';
import interceptorsReducer from './interceptorsSlice';
import vectorGraphElementsReducer from './vectorGraphElementsSlice';
import aggregationSlice from './aggregationSlice';
import logsSlice from './logsSlice';
import compositeStatesReducer from './compositeStatesSlice';
import translationSlice from './translationSlice';
import farmDetailsSlice from './farmDetailsSlice';
import dataConfigChartReducer from './dataConfigChartSlice';
import { collapsedPanelStateSlice } from './collapsedPanelStateSlice';
import snackbarSlice from './snackbarSlice';
import api from '../rtk/api';
import { AppDispatch } from '../configureStore';

/** Combine all reducers
 * @returns {Object} store
 */

export const appReducer = combineReducers({
  [api.reducerPath]: api.reducer, // RTK Query
  userDetails: userDetailsReducer,
  farmDetails: farmDetailsSlice,
  redirect: redirectReducer,
  preloader: preloaderReducer,
  eventPageDetails: eventDetailsSlice,
  eventFilters: eventFiltersSlice,
  domainsData: domainsReducer,
  selectedDomainComponentsData: componentsDataReducer,
  entityInfo: entityInfoReducer,
  entitySettings: entitySettingsReducer,
  entitySettingsOptions: entitySettingsOptionsReducer,
  eventsPageLevels: eventsLevelsSlice,
  canbusTroubleshoot: canbusTroubleshootReducer,
  canbusTroubleshootTutorial: canbusTroubleshootTutorialReducer,
  loadManager: loadManagerReducer,
  entityKpi: entityKpiDetailsReducer,
  domainModalGrid: domainModalGridReducer,
  dataCharts: dataChartReducer,
  dataConfigCharts: dataConfigChartReducer,
  dataExplorerLoadManager: dataExplorerLoadManagerReducer,
  vectorDataCharts: vectorDataChartSlice,
  vectorConfiguration: vectorConfigurationReducer,
  featureFlag: featureFlagsReducer,
  translate: translationSlice,
  eventsChartData: dataChartEventsReducer,
  eventsLevelMenuOptions: eventLevelMenuOptionsReducer,
  compositeStatesData: compositeStatesReducer,
  updateHistoryData: updateHistorySlice,
  vectorDetails: vectorDetailsReducer,
  zoom: zoomReducer,
  vectorCriticalAlarmsAndEvents: vectorCriticalAlarmsAndEventsReducer,
  tableColumns: tableColumnsReducer,
  farmsOptions: farmsOptionsReducer,
  astronautPropertySelect: astronautPropertySelectReducer,
  fencesState: fencesStateReducer,
  appliedProperties: appliedPropertiesReducer,
  tooltip: tooltipReducer,
  lineChartConfig: lineChartConfigReducer,
  colorDictionary: colorDictionaryReducer,
  canvasGeneration: canvasGenerationReducer,
  networkError: networkErrorReducer,
  astronautTooltip: astronautTooltipReducer,
  astronautSelectedActivity: astronautSelectedActivityDetailsReducer,
  verticalLine: verticalLineReducer,
  graphElements: astronautGraphElementsReducer,
  kpi: kpiReducer,
  interceptors: interceptorsReducer,
  vectorGraphElements: vectorGraphElementsReducer,
  aggregation: aggregationSlice,
  logs: logsSlice,
  collapsedPanelState: collapsedPanelStateSlice.reducer,
  snackbar: snackbarSlice,
});

export type RootState = ReturnType<typeof appReducer>;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppDispatch: () => AppDispatch = useDispatch;

const rootReducer = (
  state: any,
  action: AnyAction,
): ReturnType<typeof appReducer> => {
  // Reset all items in the storage
  if (action.type === RESET_STORAGE) {
    // Remove all data from store except application version
    const { applicationVersion, featureFlag, collapsedPanelState } = state;
    // eslint-disable-next-line no-param-reassign
    state = {
      applicationVersion,
      featureFlag,
      collapsedPanelState,
      interceptors: { status: 'initialized' },
    };
  }
  // Reset all items in storage except userDetails
  if (action.type === RESET_STORAGE_WITHOUT_USER_SESSION) {
    const { userDetails, collapsedPanelState } = state;
    // Keep userDetails and applicationVersion in store
    // eslint-disable-next-line no-param-reassign
    state = {
      userDetails,
      collapsedPanelState,
      interceptors: { status: 'initialized' },
    };
  }
  return appReducer(state, action);
};

export default rootReducer;
